import { ArrowRight, Check, Clock3, ShieldCheck, Sparkles, Target } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { money } from "../../shared/domain";
import { seedOffers } from "../../shared/seed";
import { Logo } from "../components/Logo";
import { attributionFromLocation } from "../lib/attribution";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { defaultLander, landers } from "../lib/landers";

export function LandingPage() {
  const { slug } = useParams();
  const config = slug ? landers[slug] || defaultLander : defaultLander;
  const { session, signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const examples = useMemo(() => seedOffers.slice(0, 3), []);

  useEffect(() => {
    document.title = `${config.headline} | Bucks`;
    void api.captureAttribution(attributionFromLocation(config));
  }, [config]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!accepted) {
      setError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.track({ name: "signup_started", properties: { landerId: config.id } });
      await signup(email, password);
      navigate("/onboarding/goal");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "We couldn't create your account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`landing landing-${config.accent}`}>
      <header className="public-header">
        <Logo to="/" />
        {session ? <Link className="text-link" to="/earn">Go to Earn</Link> : <Link className="text-link" to="/login">Sign in</Link>}
      </header>

      <main>
        <section className="hero section-shell">
          <div className="hero-copy">
            <p className="eyebrow">{config.eyebrow}</p>
            <h1>{config.headline}</h1>
            <p className="hero-subtitle">{config.subheadline}</p>
            <ul className="trust-list" aria-label="Bucks benefits">
              <li><Check /> Free to join</li>
              <li><Check /> Rewards shown in dollars</li>
              <li><Check /> One clear next step</li>
            </ul>
          </div>
          <div className="signup-card" id="join">
            <div>
              <span className="step-kicker">CREATE YOUR FREE ACCOUNT</span>
              <h2>Start your first goal</h2>
              <p>No subscription. Just a simple place to track what you earn.</p>
            </div>
            <form onSubmit={submit} noValidate>
              <label>Email address<input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" /></label>
              <label>Password<span className="label-help">10+ characters</span><input type="password" autoComplete="new-password" minLength={10} required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Create a password" /></label>
              <label className="checkbox-row"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span>I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</span></label>
              {error && <div className="form-error" role="alert">{error}</div>}
              <button className="button hero-button" disabled={busy}>{busy ? "Creating account…" : config.cta}<ArrowRight /></button>
            </form>
            <p className="form-footnote"><ShieldCheck /> Your earnings and activity stay tied to your Bucks account.</p>
            <p className="legal-note">Earnings and offer availability aren't guaranteed. Each reward requires verified qualifying activity.</p>
          </div>
        </section>

        <section className="account-preview section-shell" aria-labelledby="preview-title">
          <div className="section-intro">
            <p className="eyebrow">ONE BALANCE. ONE GOAL.</p>
            <h2 id="preview-title">Know exactly what to do next.</h2>
            <p>This is an illustrative account—not a promise of earnings.</p>
          </div>
          <div className="example-account">
            <span className="example-label">EXAMPLE ACCOUNT</span>
            <div className="example-balance">$7.50</div>
            <span className="available-label">AVAILABLE</span>
            <div className="example-goal-row"><span>Your $10 goal</span><strong>$7.50 / $10</strong></div>
            <div className="progress-track"><span style={{ width: "75%" }} /></div>
            <p><strong>$2.50</strong> to go</p>
            <span className="button example-button">Find me $2.50 <ArrowRight /></span>
          </div>
        </section>

        <section className="how-section">
          <div className="section-shell">
            <div className="section-intro center">
              <p className="eyebrow">HOW BUCKS WORKS</p>
              <h2>Three steps. No points math.</h2>
            </div>
            <div className="steps-grid">
              <article><span className="step-icon"><Target /></span><span className="step-number">01</span><h3>Choose a goal</h3><p>Start small and see your progress in real dollars.</p></article>
              <article><span className="step-icon"><Sparkles /></span><span className="step-number">02</span><h3>Complete offers</h3><p>Pick from clear opportunities with effort and terms shown upfront.</p></article>
              <article><span className="step-icon"><ShieldCheck /></span><span className="step-number">03</span><h3>Track what you earn</h3><p>See pending and available rewards in one account.</p></article>
            </div>
          </div>
        </section>

        <section className="opportunities section-shell">
          <div className="section-intro">
            <p className="eyebrow">EXAMPLE OPPORTUNITIES</p>
            <h2>Simple actions. Clear rewards.</h2>
            <p>Availability and qualification vary. Examples are illustrative.</p>
          </div>
          <div className="example-offer-grid">
            {examples.map((offer) => (
              <article key={offer.id} className="mini-offer">
                <strong>+{money(offer.rewardCents)}</strong>
                <h3>{offer.title}</h3>
                <p>{offer.shortDescription}</p>
                <span><Clock3 /> ~{offer.estimatedMinutes} minutes</span>
              </article>
            ))}
          </div>
        </section>

        <section className="faq-section">
          <div className="section-shell narrow">
            <div className="section-intro center"><p className="eyebrow">GOOD TO KNOW</p><h2>Questions, answered.</h2></div>
            <div className="faq-list">
              <details><summary>Is Bucks free to join?</summary><p>Yes. Bucks has no signup fee or subscription.</p></details>
              <details><summary>How do rewards work?</summary><p>Each offer shows a dollar reward and what qualifies. After a valid completion is confirmed, the reward appears as pending or available in Activity.</p></details>
              <details><summary>Do I need to make a purchase?</summary><p>Not for offers marked “No purchase required.” Other offers may have different requirements, which are shown before you start.</p></details>
              <details><summary>Are earnings guaranteed?</summary><p>No. Offer availability, eligibility, and successful completion vary. Bucks only credits verified qualifying activity.</p></details>
            </div>
            <a className="button final-cta" href="#join">{config.cta}<ArrowRight /></a>
          </div>
        </section>
      </main>

      <footer className="public-footer"><Logo to="/" /><p>Clear opportunities. Real progress.</p><nav><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/support">Support</Link></nav></footer>
      {api.demo && <div className="demo-banner">DEMO MODE · Complete offers inside Bucks to test the full loop</div>}
    </div>
  );
}
